// Die Wörter der drei Verwaltungsebenen und des Sichtwechsels an einer Stelle (SPEC VE-13, Entscheidung E-VE-1).
// Die Einträge heißen in allen Produkten gleich; nur erklärende Texte dürfen das Produktwort für „Firma“ nutzen.
import { uhrzeit } from '../utils/datum';

export const EBENEN = {
  verwaltung: 'Verwaltung',
  // Profil-Nachbau F-11 (Antwort auf F-E1): die Ebene der Firma heißt überall „Einstellungen der Firma“ (bis dahin „Admin-Einstellungen“).
  adminEinstellungen: 'Einstellungen der Firma',
  meineEinstellungen: 'Meine Einstellungen',
} as const;

// Die Seite Einstellungen im App-Rahmen (AE-1, 05.10.2026): die Ebenen sind ihre Gruppen, das Menü führt nur dorthin.
export const EINSTELLUNGEN = {
  titel: 'Einstellungen',
  plattform: 'Plattform',
  // Profil-Nachbau F-03 (Bennys Freigabe 14.10.2026): die drei Gruppen der Konto-Leiste „Meine Einstellungen“.
  gruppeKonto: 'Konto',
  gruppeArbeiten: (produkt: string) => `Arbeiten mit ${produkt}`,
  gruppeNutzungDaten: 'Nutzung und Daten',
  // Profil-Nachbau F-26, Nachtrag (19.10.2026): Überschrift des ersten Abschnitts der Leiste im Fenster „Einstellungen“
  // (components/EinstellungenFenster.tsx); die beiden anderen heißen BEGRIFFE.firma und EINSTELLUNGEN.plattform.
  abschnittKonto: 'Mein Konto',
  // Profil-Nachbau F-22 (Standard F-22-1, 15.10.2026): der Weg heißt wie der Menüeintrag MENUE.plattformVerwaltung mit „öffnen“,
  // auf der Startseite ohne Platz, in „Mein Zugang“ und in der Gruppe „Plattform“ des Pop-ups; bis F-22 „Verwaltung öffnen“. Als
  // ganze Zeichenkette, weil MENUE erst weiter unten steht (welle1-orientierung.test.mjs prüft die Gleichheit).
  verwaltungOeffnen: 'Plattform-Verwaltung öffnen',
  // Nachbesserung B7: derselbe Weg für Konten ohne Plattform, aber mit Einstellungen der Firma.
  adminEinstellungenOeffnen: `${EBENEN.adminEinstellungen} öffnen`,
  // Plan 1a 1A-5 (Bennys Entscheidung 09.10.2026): nur der Betreiber wechselt die Sicht; die eigene Fassung für die
  // Besitzerin oder den Besitzer einer Kundenfirma entfällt (W1-O03).
  sichtWechseln: 'Sicht wechseln',
  // Nachbesserung B6: der Abschnitt des Bereichs „Sicht wechseln“ mit dem Schalter (Name wie das Menü des Schalters).
  sichtWaehlen: 'Sicht wählen',
  alle: 'Alle Einstellungen',
  // Profil-Nachbau F-26 (19.10.2026, ersetzt „Zurück zum Chat“ aus F-19): der Knopf mit X oben rechts in jedem Fenster der
  // Bereichs-Ebene (Einstellungen, KI-Cockpit, Hilfe); führt zur gemerkten Herkunft zurück (components/bereichsEbene.ts). Das
  // Wort steht nur hier; das Fenster der Erklärungen (ERKLAERUNG.schliessen) nimmt es mit.
  bereichSchliessen: 'Schließen',
  // AE-1 Nachtrag 3 (SPEC AE-07): Bereich und Menüeintrag „Über {Produktname}“ (MK-1: immer CLAiRIS).
  ueber: (produkt: string) => `Über ${produkt}`,
  // AE-1 Nachtrag 3 (SPEC AE-02): Name des Schließen-Knopfs im Pop-up.
  schliessen: 'Einstellungen schließen',
  // Nachbesserung B11: der Firmenkontext ließ sich nicht laden (etwa 429 an der Ratengrenze).
  bereicheFehlen: 'Die Bereiche konnten nicht geladen werden.',
  // Plan 1a Welle 1, G3-6 (4.04): beim allerersten Laden statt einer leeren Gruppe.
  bereicheLaden: 'Bereiche werden geladen …',
  erneutVersuchen: 'Erneut versuchen',
} as const;
// AE-1 Nachtrag 3 (SPEC AE-13, Vorbild claude.ai): die Wörter des Profilmenüs; „Über …“ steht unter EINSTELLUNGEN.
// Profil-Nachbau F-01 (Bennys Freigabe 14.10.2026, Vorbild meinGPT): „Meine Nutzung“ und „Mehr erfahren“ entfallen aus dem
// Menü; „Alle Tarife anzeigen“ bleibt als Knopf in „Tarif und Kosten“.
export const MENUE = {
  // Profil-Nachbau F-04 (Bennys Freigabe 14.10.2026, „KI-Cockpit bitte den Namen beibehalten“): wer Personen ansehen darf
  // (ebenen.adminEinstellungen.teamNutzung), liest im Menü „KI-Cockpit“; Ziel bis zum Firmen-Cockpit (F-08) ist der Bereich
  // „Team-Nutzung“ (GF-4). Seitentitel des Cockpits und Leisteneintrag der Plattform-Verwaltung nehmen dasselbe Wort.
  kiCockpit: 'KI-Cockpit',
  // Bis F-04 das Menüwort; bleibt Suchwort der Einstellungen-Suche (pages/einstellungen/seiten.ts), damit das alte Wort zur Team-Nutzung führt.
  nutzungTeam: 'Nutzung im Team',
  // F-01: der Menüeintrag zur Konsole des Betreibers; der Eintrag zu den Einstellungen der Firma nimmt EBENEN.adminEinstellungen.
  plattformVerwaltung: 'Plattform-Verwaltung',
  // F-23 (PF-NB-verwaltung-6, Betreiber H-01): die Zusatzzeile unter dem Eintrag, Beschreibung, nicht Teil des Namens.
  plattformVerwaltungZusatz: 'Alle Kundenfirmen, Kosten und Support',
  // Der Bereich „Hilfe“ im Pop-up; im Menü heißt der Eintrag „Hilfe und Feedback“ (F-01) und nimmt den Inhalt von
  // „Mehr erfahren“ auf. Seit PF-104 ist er ein Link auf die Hilfe-Seite /hilfe (F-06, F-19), die dasselbe Wort trägt
  // (components/hilfe/hilfeTexte.ts).
  hilfe: 'Hilfe',
  hilfeUndFeedback: 'Hilfe und Feedback',
  // Plan 1a Welle 1, G3-3 (D-13, W1-O04; M1-34 „das ist keine Hilfe – ich habe ja keinen Fehler, ich verstehe nur ein Wort
  // nicht“): der erste Unterpunkt öffnet die Anleitungen. Seit PF-104 steht es nicht mehr im Menü; Verwender ist der Abschnitt
  // der Anleitungen auf /hilfe/dokumente (components/hilfe/hilfeTexte.ts).
  anleitungen: 'Anleitungen und häufige Fragen',
  tarife: 'Alle Tarife anzeigen',
  // Seit PF-104 nicht mehr im Menü; Verwender ist der Link auf /hilfe/dokumente (components/hilfe/hilfeTexte.ts).
  datenschutz: 'Ihre Datenschutzeinstellungen',
  abmelden: 'Abmelden',
} as const;
// F-23 (Fehlersuche Nr. 20, Nutzerblick Firma H-07, Mitglied H-03): ein Wort je Ding (Standard F-23-rahmen-1 und -2). Die
// Kundenfirma heißt „Firma“ (wie „Einstellungen der Firma“, F-11), wer sie verwaltet „die Verwaltung Ihrer Firma“; die Person der
// Firma „KI-Verantwortliche:r“ (F-20), die des Teams „KI-Champion“ (F-14). „Anfrage“ allein ist die an den Support (F-06), die
// Bitte um mehr Kontingent die „Kontingent-Anfrage“ (F-15), „Anfrage an die KI“ nie ohne „an die KI“. Die Ratsche
// frontend/tests/begriffe-woerter.test.mjs hält das.
export const BEGRIFFE = {
  firma: 'Firma',
  firmen: 'Firmen',
  verwaltungDerFirma: 'Verwaltung Ihrer Firma',
  kiVerantwortliche: 'KI-Verantwortliche:r',
  kiChampion: 'KI-Champion',
  supportAnfrage: 'Anfrage',
  kontingentAnfrage: 'Kontingent-Anfrage',
  kontingentAnfragen: 'Kontingent-Anfragen',
  recherche: 'Recherche',
  recherchen: 'Recherchen',
} as const;
// Profil-Nachbau F-24 (GRUNDREGELN 1e, 16.10.2026, Vorbild meinGPT): die Wörter der drei Erklär-Bausteine (components/Erklaerung.tsx).
// tipp ist der Name des Info-Symbols („Erklärung: Kontingent“), ohne Wort nur „Erklärung“.
export const ERKLAERUNG = {
  tipp: (wort: string) => (wort ? `Erklärung: ${wort}` : 'Erklärung'),
  mehrInDerHilfe: 'Mehr in der Hilfe',
  schliessen: EINSTELLUNGEN.bereichSchliessen,
} as const;
// Plan 1a Welle 1 (W1-O07, D-23): der Satz „Recherchieren ist mit Ihrem Konto gerade nicht möglich.“ allein entfällt; die
// Startansicht ohne Recherche zeigt Titel, Satz und Weg der Statuszeile (pages/accessSummary.ts startseite).

// Stiller Rückfall auf die erste vorhandene Ebene (VE-06, M-18.3).
export const HINWEIS_RUECKFALL = 'Die Verwaltung ist für Ihr Konto nicht freigeschaltet. Sie sehen die Einstellungen der Firma.';
export const HINWEIS_EBENE_FEHLT = 'Diese Ebene ist für Ihr Konto nicht freigeschaltet.';

// Sichtwechsel (VE-17, VE-19, VE-28). Die Linse ist ein Bündel, kein Rollenname (A-5): die Werte heißen
// TENANT_ADMIN und TENANT_USER. Seit Plan 1a MA-02 (W-16) sagt die Oberfläche dieselben Wörter wie überall:
// „Administrator“ und „Mitglied“. Die Meldung des Servers (backend/src/administration/operator/viewNotice.ts) muss
// gleich lauten (Wächter backend/tests/administration/sicht-quelltext.test.cjs, B7).
export type Linse = 'TENANT_ADMIN' | 'TENANT_USER';
export const LINSEN: Record<Linse, string> = { TENANT_ADMIN: 'Administrator', TENANT_USER: 'Mitglied' };
export const SICHT_BETREIBER = 'Betreiber';
export const RUECKWEG_TEXT = 'Zurück zu meiner Sicht';
export const INHALTE_GESPERRT = 'Inhalte der Personen sind in dieser Sicht gesperrt.';
export const NICHT_LESBAR = 'In dieser Sicht nicht lesbar.';
export const NUR_LESEND_GRUND = (linse: Linse) => `Nur lesend: Sie sehen das als ${LINSEN[linse]}`;
// Plan 1a Welle 1 (W1-18, M1-50 „Sehe ich dann ihre Akten?“, O2-2 Inhalt): der Satz sagt vor dem Start, was ausgeblendet
// bleibt; er deckt sich mit INHALTE_GESPERRT und VE-25 (Recherchen, Antworten und Dokumente der Personen öffnet keine Sicht).
export const SICHT_AUSGEBLENDET = 'Recherchen, Antworten und Dokumente der Personen bleiben ausgeblendet.';
// Plan 1a 1A-5: nur noch der Satz für den Betreiber (der Satz für die Besitzerin oder den Besitzer entfällt).
export const SICHT_SATZ = `Die Sicht zeigt das Produkt so, wie es ein Administrator oder ein Mitglied der Firma sieht – nur lesend, höchstens 30 Minuten, protokolliert. ${SICHT_AUSGEBLENDET}`;
/** W1-18 (O2-2 Inhalt): der Satz einer Vertretung im Startdialog, mit Firma und gewählter Linse. */
export const VERTRETUNG_SATZ = (firma: string, linse: Linse) =>
  `Vertretung: Sie sehen ${firma} so, wie ein ${LINSEN[linse]} sie sieht – nur lesend, höchstens 30 Minuten, protokolliert. ${SICHT_AUSGEBLENDET} Die Firma wird mit Ihrer Begründung benachrichtigt.`;
// Plan 1a Welle 2, Ä-21 (spec-note-1/SPEC-INHABER.md Ä-21 Punkte 1, 2 und 4; INH-E-26, INH-E-27, INH-E-44): der Einstieg
// „Sicht starten …“ aus der Zeile einer Person unter „Alle Benutzer“ und von der Firmenseite der Konsole und die Sätze des
// kürzeren Startdialogs, wenn die Firma feststeht. Die Sätze darüber (SICHT_AUSGEBLENDET, SICHT_SATZ, VERTRETUNG_SATZ) bleiben
// Zeichen für Zeichen (Schutzliste). Geprüft in frontend/tests/welle2-inhaber.test.mjs.
export const SICHT_EINSTIEG = 'Sicht starten …';
/** Zugänglicher Name des Knopfs in der Zeile einer Person: „Sicht von Gertraud Steindl starten, nur lesend“. */
export const sichtVonPerson = (name: string) => `Sicht von ${name} starten, nur lesend`;
/** Vorwahl der Linse aus der Zeile einer Person: Besitzer und Administrator sehen als Administrator, sonst als Mitglied. Die
 *  Wahl bleibt änderbar; eine Sicht „als genau diese Person“ gibt es nicht (Inhalte der Personen bleiben gesperrt). */
export const linseVorwahl = (person: { besitzer?: boolean; administrator?: boolean }): Linse => person.besitzer || person.administrator ? 'TENANT_ADMIN' : 'TENANT_USER';
/** Nachbesserung P/S-02 (VORAB-MESSUNG 7.1 Nr 4; welle-2/nachbesserung/AUFTEILUNG.md A-11, W-5): der zweite Satz allein, nur mit
 *  bekanntem Namen des Besitzers und der Linse „Administrator“ (sonst null). sichtFirmaSatz setzt sich daraus zusammen; bei
 *  einer Vertretung aus der Kopfleiste steht er allein vor dem gelben Satz. */
export const sichtBesitzerSatz = (besitzer: string | null | undefined, linse: Linse): string | null =>
  besitzer && linse === 'TENANT_ADMIN' ? `Was nur ${besitzer} als Besitzer sieht, etwa „Besitz übertragen …“, fehlt in dieser Sicht.` : null;
/** Erster Satz des Dialogs bei feststehender Firma; er folgt der gewählten Linse. Ist der Name des Besitzers bekannt (aus der
 *  Zeile einer Person, die Besitzer ist, oder seit P/S-02 von der Firmenseite) und die Linse „Administrator“, sagt der zweite
 *  Satz, was in der Sicht fehlt (gezeigt wird die Sicht eines Administrators, nicht die der Person). Sonst nur der erste Satz. */
export const sichtFirmaSatz = (firma: string, linse: Linse, besitzer?: string | null) => {
  const zweiter = sichtBesitzerSatz(besitzer, linse);
  return `Sie sehen ${firma} mit den Rechten eines ${LINSEN[linse]}s.${zweiter ? ` ${zweiter}` : ''}`;
};
/** Die feststehende Firma als Text statt einer Auswahl mit Suche. */
export const sichtFirmaZeile = (firma: string) => `Firma: ${firma}`;
/** Die Zusatzrolle steht im zugeklappten Aufklapper; seine Zeile nennt die Wahl, damit sie zugeklappt nicht unsichtbar ist. */
export const ZUSATZROLLE = { feld: 'Zusatzrolle (optional)', ohne: 'Ohne Zusatzrolle' } as const;
export const zusatzrolleZeile = (rolle?: string | null) => `${ZUSATZROLLE.feld}: ${rolle || ZUSATZROLLE.ohne}`;

type Aktiv = { art?: 'PREVIEW' | 'IMPERSONATION'; linse: Linse; firmaName: string; zusatzrolle?: { id: string; name: string } | null };
// Das Profilfeld sicht.starten (backend operator/sicht.ts startRechte). nurUser liefert der Server nach 1A-5 immer false;
// die Oberfläche fragt es nicht mehr ab.
export type Starten = { vertretung: boolean; vorschau: boolean };

// Plan 1a MA-02 (W-21) und E23: Titel und Knopf des Startdialogs (seit 1A-5 nur noch der Betreiber). Seit der Nachbesserung
// P/Z-06 steht statt des festen Satzes zu Begründung und Passwort im festen Fuß „Es fehlt noch: …“ mit dem,
// was wirklich fehlt (sichtStartPruefung); der Knopf ist gesperrt, solange etwas fehlt.
export const SICHT_START = {
  titel: 'Sicht starten',
  knopf: 'Sicht starten',
  legende: 'Sicht als',
} as const;
/** Nachbesserung P/Z-06 (VORAB-MESSUNG 7.1 Nr 5; GB-02; welle-2/nachbesserung/AUFTEILUNG.md A-12, W-6): die Prüfung des
 *  Startdialogs nach der Regel des Servers (Vertretung: Begründung ohne Leerzeichen am Rand 10 bis 500 Zeichen und das eigene
 *  Passwort; die Vorschau der eigenen Firma fragt beides nicht). fehler.grund: eine eingetragene, aber zu kurze Begründung (die
 *  Oberfläche zeigt sie erst nach Verlassen des Felds); fehlt: „Firma“, „Begründung“, „Ihr Passwort“ in dieser Reihenfolge. */
export const SICHT_START_MELDUNG = { grund: 'Bitte mindestens 10 Zeichen eintragen.' } as const;
export function sichtStartPruefung(eingabe: { firma: boolean; vertretung: boolean; grund: string; passwort: string }): { fehler: { grund?: string }; fehlt: string[] } {
  const laenge = eingabe.grund.trim().length;
  const fehler = { grund: eingabe.vertretung && laenge > 0 && laenge < 10 ? SICHT_START_MELDUNG.grund : undefined };
  const fehlt = [eingabe.firma ? '' : 'Firma', eingabe.vertretung && laenge < 10 ? 'Begründung' : '', eingabe.vertretung && !eingabe.passwort ? 'Ihr Passwort' : ''].filter(Boolean);
  return { fehler, fehlt };
}

/** Titel des Navigationseintrags und des Bereichs (1A-5: immer „Sicht wechseln“; nur der Betreiber sieht ihn). */
export function sichtTitel(_starten?: Starten | null): string {
  return EINSTELLUNGEN.sichtWechseln;
}
/** Der Satz unter dem Titel und im Startdialog (1A-5: nur noch der Satz für den Betreiber). */
export function sichtSatz(_starten?: Starten | null): string {
  return SICHT_SATZ;
}

export function bandTitel(aktiv: Aktiv): string {
  const rolle = aktiv.zusatzrolle ? ` (Zusatzrolle: ${aktiv.zusatzrolle.name})` : '';
  return aktiv.art === 'PREVIEW'
    ? `Vorschau: Sie sehen das Produkt als ${LINSEN[aktiv.linse]}${rolle}`
    : `Vertretung: Sie sehen ${aktiv.firmaName} als ${LINSEN[aktiv.linse]}${rolle}`;
}
// Schlusslauf 2, N4 (Rest von Befund 10): der Name ohne technische Kontonummer.
export const bandKonto = (name: string) => `Sie bleiben ${name}. Nur lesend.`;

const minuten = (bis: Date, jetzt: Date) => Math.max(0, Math.ceil((bis.getTime() - jetzt.getTime()) / 60000));
export const bandFrist = (bis: Date, jetzt: Date) => `Gültig bis ${uhrzeit(bis)} · noch ${minuten(bis, jetzt)} Minuten`;
// Ankündigung erst in den letzten fünf Minuten (VE-28).
export function restText(bis: Date, jetzt: Date): string | null {
  const rest = bis.getTime() - jetzt.getTime();
  if (rest > 5 * 60000) return null;
  const m = minuten(bis, jetzt);
  return m <= 1 ? 'Die Sicht endet in 1 Minute.' : `Die Sicht endet in ${m} Minuten.`;
}
// Beschriftung des Schalters (VE-17, P-5); null: kein Schalter. Plan 1a MA-02 (W-18): in einer laufenden Sicht
// „Ansicht als Administrator · Firma“, ohne Sicht „Sicht: Betreiber“. Seit 1A-5 hat nur ein Betreiberkonto ein Startrecht.
export function schalterText(aktiv: Pick<Aktiv, 'linse' | 'firmaName'> | null | undefined, starten: Starten | null | undefined): string | null {
  if (aktiv) return `Ansicht als ${LINSEN[aktiv.linse]} · ${aktiv.firmaName}`;
  if (!starten || (!starten.vertretung && !starten.vorschau)) return null;
  return `Sicht: ${SICHT_BETREIBER}`;
}
// Die eigene Sicht im Menü des Schalters: „Betreiber“ (VE-33 und W8 mit dem Eintrag „Besitzer“ entfallen durch 1A-5).
export const eigeneSicht = (_starten?: Starten) => SICHT_BETREIBER;
// Die Linsen, die das Menü der Sicht zum Start anbietet: aus einer laufenden Sicht keine (VE-26), ohne Startrecht keine,
// sonst beide (1A-5: nur der Betreiber hat ein Startrecht).
export function startLinsen(aktiv: boolean, starten: Starten): Linse[] {
  if (aktiv || (!starten.vertretung && !starten.vorschau)) return [];
  return ['TENANT_ADMIN', 'TENANT_USER'];
}
